import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Inbox, Phone, Mail, MapPin, Trash2, CalendarClock, Flame, Clock, History, X } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import useLocalStorageState from "../hooks/useLocalStorageState";
import ConfirmDialog from "../components/ConfirmDialog";

const STATUSES = ["new", "contacted", "converted", "closed"];
const COLUMNS = [
  { key: "idx", label: "#", required: true },
  { key: "name", label: "Name", required: true },
  { key: "business_name", label: "Business" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Date" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];

const statusColor = {
  new: "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, closed: 0, this_week: 0, top_service: null, top_service_count: 0 });
  const [inquiries, setInquiries] = useState([]);
  const [statusSel, setStatusSel] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_inquiries_hidden_cols_v1", new Set(["updated_by_label", "updated_at"]));
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);
  const toggleStatus = (s) => { setPage(1); setStatusSel((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; }); };
  const onSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir("asc"); } };

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        api.get("/inquiries/stats"),
        api.get("/inquiries"),
      ]);
      setStats(s.data);
      setInquiries(list.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (statusSel.size === 0) return inquiries;
    return inquiries.filter((i) => statusSel.has(i.status));
  }, [inquiries, statusSel]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    return arr;
  }, [filtered, sortKey, sortDir]);
  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize), [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, statusSel.size]);

  // Inquiries created in the last 24 hours, max 4 for the timeline card
  const recentInquiries = inquiries
    .filter((i) => {
      const created = new Date(i.created_at).getTime();
      return Date.now() - created <= 24 * 60 * 60 * 1000;
    })
    .slice(0, 4);

  const askRemove = (inq) => setDeleting(inq);
  const confirmRemove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/inquiries/${deleting.id}`);
      toast.success("Inquiry deleted");
      setSelected((s) => (s?.id === deleting.id ? null : s));
      load();
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  // Status change with remark
  const [statusChange, setStatusChange] = useState(null); // { inquiry, toStatus }
  const [remark, setRemark] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const promptStatusChange = (inq, toStatus) => {
    if (inq.status === toStatus) return;
    setStatusChange({ inquiry: inq, toStatus });
    setRemark("");
  };
  const submitStatusChange = async (e) => {
    e?.preventDefault?.();
    if (!statusChange) return;
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/inquiries/${statusChange.inquiry.id}`, {
        status: statusChange.toStatus,
        remark: remark.trim(),
      });
      toast.success(`Marked as ${statusChange.toStatus}`);
      setSelected((s) => (s?.id === data.id ? data : s));
      setStatusChange(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="p-6 lg:p-10 w-full">
      <div className="mb-8">
        <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Inquiries</h1>
      </div>

      {/* Highlight Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div data-testid="stat-this-week" className="card-reveal card-reveal-1 bg-[#0A0A0A] text-white p-6 flex items-center gap-5">
          <div className="bg-[var(--brand-primary)]/15 p-3 rounded-sm">
            <CalendarClock size={26} strokeWidth={1.5} className="text-[var(--brand-primary)]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50">Inquiries this week</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="font-display text-4xl font-bold tracking-tight">{stats.this_week}</div>
              <div className="text-xs text-white/40">last 7 days</div>
            </div>
          </div>
        </div>
        <div data-testid="stat-top-service" className="card-reveal card-reveal-2 bg-white border border-black/10 p-6 flex items-center gap-5">
          <div className="bg-[var(--brand-primary)]/10 p-3 rounded-sm">
            <Flame size={26} strokeWidth={1.5} className="text-[var(--brand-primary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">Top requested service</div>
            {stats.top_service ? (
              <>
                <div className="font-display text-xl font-bold tracking-tight mt-1 truncate">{stats.top_service}</div>
                <div className="text-xs text-[#525252] mt-0.5">{stats.top_service_count} {stats.top_service_count === 1 ? "request" : "requests"}</div>
              </>
            ) : (
              <div className="text-sm text-[#525252] mt-1">No requests yet</div>
            )}
          </div>
        </div>
        <RecentInquiriesCard inquiries={recentInquiries} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard label="Total" value={stats.total} testId="stat-total" delay={3} />
        <StatCard label="New" value={stats.new} testId="stat-new" accent delay={4} />
        <StatCard label="Contacted" value={stats.contacted} testId="stat-contacted" delay={5} />
        <StatCard label="Converted" value={stats.converted} testId="stat-converted" delay={6} />
        <StatCard label="Closed" value={stats.closed} testId="stat-closed" delay={7} />
      </div>

      {/* Filter + columns toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252]">Status:</div>
        <MultiSelectStatusFilter options={STATUSES} selected={statusSel} onToggle={toggleStatus} testIdPrefix="inquiry-filter"/>
        {statusSel.size > 0 && (
          <button data-testid="inquiry-clear-filter" onClick={() => { setPage(1); setStatusSel(new Set()); }} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="inquiries-cols"/></div>
      </div>

      {loading ? (
        <div className="text-[#525252]" data-testid="inquiries-loading">Loading inquiries...</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center" data-testid="inquiries-empty">
          <Inbox size={36} className="mx-auto text-[#525252]" />
          <p className="mt-3 text-[#525252]">No inquiries match the current filter.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("idx") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252] w-12">#</th>}
                  {showCol("name") && <th className="px-4 py-3 text-left"><SortableHeader label="Name" sortKey="name" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("business_name") && <th className="px-4 py-3 text-left"><SortableHeader label="Business" sortKey="business_name" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("phone") && <th className="px-4 py-3 text-left"><SortableHeader label="Phone" sortKey="phone" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("status") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="status" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Date" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((inq, i) => (
                  <tr
                    key={inq.id}
                    onClick={() => setSelected(inq)}
                    data-testid={`inquiry-row-${inq.id}`}
                    className="border-b border-black/5 hover:bg-[#FAFAFA] cursor-pointer"
                  >
                    {showCol("idx") && <td className="px-4 py-3 text-[#525252] text-xs">{(page - 1) * pageSize + i + 1}</td>}
                    {showCol("name") && <td className="px-4 py-3 font-medium truncate max-w-[220px]" title={inq.name}>{inq.name}</td>}
                    {showCol("business_name") && <td className="px-4 py-3 text-[#525252] truncate max-w-[220px]">{inq.business_name}</td>}
                    {showCol("phone") && <td className="px-4 py-3 text-[#525252]">{inq.phone}</td>}
                    {showCol("status") && (
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wider rounded-sm border ${statusColor[inq.status]}`}>{inq.status}</span>
                      </td>
                    )}
                    {showCol("created_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{new Date(inq.created_at).toLocaleDateString()}</td>}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-[#525252] text-xs truncate max-w-[200px]" title={inq.updated_by_label}>{inq.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{inq.updated_at ? new Date(inq.updated_at).toLocaleString() : "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
      )}

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="inquiry-detail-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between">
              <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wider rounded-sm border ${statusColor[selected.status]}`}>{selected.status}</span>
              <button onClick={() => setSelected(null)} className="text-sm text-[#525252]" data-testid="inquiry-drawer-close">Close</button>
            </div>
            <h2 className="font-display text-2xl font-bold mt-4">{selected.name}</h2>
            <div className="text-[#525252]">{selected.business_name}</div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${selected.phone}`} className="hover:text-[var(--brand-primary)]">{selected.phone}</a></div>
              {selected.email && <div className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${selected.email}`} className="hover:text-[var(--brand-primary)]">{selected.email}</a></div>}
              {selected.city && <div className="flex items-center gap-2"><MapPin size={14} /> {selected.city}</div>}
            </div>

            {selected.services?.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">Services interested</div>
                <div className="flex flex-wrap gap-2">
                  {selected.services.map((s) => (
                    <span key={s} className="text-xs bg-[#FAFAFA] border border-black/10 px-2 py-1">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {selected.message && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">Message</div>
                <p className="text-sm text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            )}

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Update status</div>
              <div className="grid grid-cols-2 gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => promptStatusChange(selected, s)}
                    disabled={selected.status === s}
                    data-testid={`update-status-${s}`}
                    className={`text-sm px-3 py-2 rounded-sm border capitalize transition-colors ${
                      selected.status === s
                        ? "bg-[#0A0A0A] text-white border-[#0A0A0A] cursor-default"
                        : "bg-white border-black/10 hover:border-black/30"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Status audit trail */}
            <div className="mt-8 border-t border-black/5 pt-6">
              <button
                type="button"
                data-testid="toggle-history"
                onClick={() => setShowHistory((v) => !v)}
                className="w-full flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#525252] hover:text-[#0A0A0A]"
              >
                <span className="inline-flex items-center gap-2"><History size={14}/> Status audit trail · {(selected.status_history || []).length}</span>
                <span className="font-mono text-[10px]">{showHistory ? "▲" : "▼"}</span>
              </button>
              {showHistory && (
                <div className="mt-4" data-testid="status-history-list">
                  {(selected.status_history || []).length === 0 ? (
                    <p className="text-sm text-[#525252]" data-testid="status-history-empty">No status changes yet.</p>
                  ) : (
                    <ul className="space-y-3">
                      {[...(selected.status_history || [])].reverse().map((h, idx) => (
                        <li key={`${h.changed_at}-${h.to_status}-${idx}`} data-testid={`history-entry-${idx}`} className="border-l-2 border-[var(--brand-primary)]/30 pl-3 py-1">
                          <div className="flex items-center gap-2 text-xs">
                            <span className={`inline-block px-1.5 py-0.5 uppercase tracking-wider rounded-sm border ${statusColor[h.from_status] || ""}`}>{h.from_status}</span>
                            <span className="text-[#525252]">→</span>
                            <span className={`inline-block px-1.5 py-0.5 uppercase tracking-wider rounded-sm border ${statusColor[h.to_status] || ""}`}>{h.to_status}</span>
                          </div>
                          {h.remark && (
                            <p className="mt-1.5 text-sm text-[#0A0A0A] whitespace-pre-wrap" data-testid={`history-remark-${idx}`}>“{h.remark}”</p>
                          )}
                          <div className="mt-1.5 text-[10px] uppercase tracking-wider text-[#525252]">
                            by <span className="font-medium normal-case">{h.changed_by_label}</span>
                            {h.changed_by_role && <span className="ml-1">({h.changed_by_role})</span>}
                            <span className="ml-2">· {new Date(h.changed_at).toLocaleString()}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => askRemove(selected)}
              data-testid="delete-inquiry-button"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 text-sm border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 px-4 py-2 rounded-sm"
            >
              <Trash2 size={14} /> Delete inquiry
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete this inquiry?"
        description={deleting ? `Inquiry from “${deleting.name}” (${deleting.business_name || "—"}) will be permanently removed.` : ""}
        confirmText="Delete"
        destructive
        onConfirm={confirmRemove}
        testId="delete-inquiry-dialog"
      />

      {/* Status change with remark */}
      {statusChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="status-change-dialog">
          <form onSubmit={submitStatusChange} className="w-full max-w-md bg-white border border-black/10 rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-bold">Update status</h3>
              <button type="button" onClick={() => { setStatusChange(null); setRemark(""); }} className="p-1 hover:bg-black/5" data-testid="status-change-close"><X size={18}/></button>
            </div>
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className={`inline-block px-1.5 py-0.5 uppercase tracking-wider rounded-sm border ${statusColor[statusChange.inquiry.status]}`}>{statusChange.inquiry.status}</span>
              <span className="text-[#525252]">→</span>
              <span className={`inline-block px-1.5 py-0.5 uppercase tracking-wider rounded-sm border ${statusColor[statusChange.toStatus]}`}>{statusChange.toStatus}</span>
            </div>
            <p className="mt-3 text-sm text-[#525252]">For <strong className="text-[#0A0A0A]">{statusChange.inquiry.name}</strong> ({statusChange.inquiry.business_name})</p>

            <label className="block text-sm font-medium mt-5 mb-2">Remark <span className="text-[#525252] font-normal">(optional)</span></label>
            <textarea
              data-testid="status-change-remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="e.g., Spoke to owner, sent proposal via WhatsApp. Quoted ₹15k for 6-week package."
              className="w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"
            />
            <p className="text-xs text-[#525252] mt-1">{remark.length}/500</p>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                data-testid="status-change-cancel"
                onClick={() => { setStatusChange(null); setRemark(""); }}
                className="flex-1 bg-white text-[#0A0A0A] border border-black/10 hover:bg-black/5 px-4 py-2.5 rounded-sm font-medium"
              >Cancel</button>
              <button
                type="submit"
                disabled={savingStatus}
                data-testid="status-change-submit"
                className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-4 py-2.5 rounded-sm font-medium disabled:opacity-60"
              >{savingStatus ? "Saving..." : "Update"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testId, accent, delay = 1 }) {
  return (
    <div data-testid={testId} className={`bg-white border border-black/10 p-5 card-reveal card-reveal-${delay}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">{label}</div>
      <div className={`mt-2 font-display text-4xl font-bold tracking-tight ${accent ? "text-[var(--brand-primary)]" : ""}`}>{value}</div>
    </div>
  );
}

function timeAgo(iso) {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function RecentInquiriesCard({ inquiries }) {
  return (
    <div data-testid="recent-inquiries-card" className="card-reveal card-reveal-3 bg-white border border-black/10 p-6 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="bg-[var(--brand-primary)]/10 p-2 rounded-sm">
          <Clock size={18} strokeWidth={1.75} className="text-[var(--brand-primary)]" />
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">Recent · last 24h</div>
      </div>
      {inquiries.length === 0 ? (
        <div className="text-sm text-[#525252] mt-2 flex-1 flex items-center" data-testid="recent-empty">No new inquiries in the last 24 hours.</div>
      ) : (
        <ul className="space-y-2 flex-1" data-testid="recent-list">
          {inquiries.map((i) => (
            <li key={i.id} className="flex items-start justify-between gap-3 text-sm border-b last:border-b-0 border-black/5 pb-2 last:pb-0">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{i.name}</div>
                <div className="text-xs text-[#525252] truncate">{i.business_name}</div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#525252] whitespace-nowrap">{timeAgo(i.created_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
